<template>
  <slot
    name="trigger"
    v-bind="{ open }"
  />
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="modal"
        class="modal-mask"
        v-bind="attrs"
      >
        <div
          ref="modalContainer"
          v-click-outside="handleClickoutside"
          class="modal-mask__container"
          :style="{ width: `${width}px` }"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="
            labelledBy || (title && !hasSlot['header'] ? titleId : undefined)
          "
          :aria-label="
            !labelledBy && (!title || hasSlot['header'])
              ? ariaLabel || undefined
              : undefined
          "
          tabindex="-1"
          @keydown="handleKeydown"
        >
          <div class="modal-mask__container--header">
            <slot name="header">
              <p
                :id="titleId"
                class="modal-mask__container--header--title"
              >
                {{ title }}
              </p>
            </slot>
            <div class="modal-mask__container--header--end">
              <slot name="header-actions" />
              <BaseButton
                data-qa="modal-close-button"
                variant="ghost"
                icon
                size="small"
                class="modal-mask__container--header--close-button"
                aria-label="Close"
                @click="close"
              >
                <CloseIcon aria-hidden="true" />
              </BaseButton>
            </div>
          </div>

          <div class="modal-mask__container--body">
            <slot />
          </div>

          <div
            v-if="hasSlot['footer']"
            class="modal-mask__container--footer"
          >
            <slot
              name="footer"
              v-bind="{ close }"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, useAttrs, useId } from 'vue'
import CloseIcon from '@shared/assets/icon/CloseIcon.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import useVueSlots from '@shared/composables/useVueSlots.js'
import Tracker from '@shared/Tracker.js'

const modal = ref(false)
const modalContainer = ref(null)
const readyForClickOutside = ref(false)
const previouslyFocusedElement = ref(null)
const previousBodyOverflow = ref('')
const titleId = useId()
let clickOutsideTimer
const emit = defineEmits(['open', 'close'])

const attrs = useAttrs()

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
  labelledBy: {
    type: String,
    default: '',
  },
  width: {
    type: Number,
    default: 592,
  },
  canClickOutside: {
    type: Boolean,
    default: false,
  },
  screenName: {
    type: String,
    default: '',
  },
})

const { hasSlot } = useVueSlots(['header', 'footer', 'default'])

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const getFocusableElements = () =>
  [
    ...(modalContainer.value?.querySelectorAll(FOCUSABLE_SELECTOR) ?? []),
  ].filter((element) => element.getAttribute('aria-hidden') !== 'true')

const restoreDocumentScroll = () => {
  document.body.style.overflow = previousBodyOverflow.value
}

const restoreFocus = async () => {
  const element = previouslyFocusedElement.value
  previouslyFocusedElement.value = null

  await nextTick()
  if (!modal.value && element?.isConnected) {
    element.focus()
  }
}

const close = () => {
  if (!modal.value) return

  modal.value = false
  readyForClickOutside.value = false
  window.clearTimeout(clickOutsideTimer)
  restoreDocumentScroll()
  emit('close')
  restoreFocus()
}

const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    event.preventDefault()
    close()
    return
  }

  if (event.key !== 'Tab') return

  const focusableElements = getFocusableElements()
  if (focusableElements.length === 0) {
    event.preventDefault()
    modalContainer.value?.focus()
    return
  }

  const firstElement = focusableElements[0]
  const lastElement = focusableElements.at(-1)
  const activeElement = document.activeElement

  if (
    event.shiftKey &&
    (activeElement === firstElement ||
      !modalContainer.value?.contains(activeElement))
  ) {
    event.preventDefault()
    lastElement.focus()
  } else if (
    !event.shiftKey &&
    (activeElement === lastElement ||
      !modalContainer.value?.contains(activeElement))
  ) {
    event.preventDefault()
    firstElement.focus()
  }
}

const open = async () => {
  if (modal.value) return

  previouslyFocusedElement.value =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
  previousBodyOverflow.value = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  modal.value = true

  if (props.screenName) {
    Tracker.trackViewScreen(props.screenName)
  }

  emit('open')

  await nextTick()
  const [firstFocusableElement] = getFocusableElements()
  const focusTarget = firstFocusableElement ?? modalContainer.value
  focusTarget?.focus()

  clickOutsideTimer = window.setTimeout(() => {
    readyForClickOutside.value = true
  }, 0)
}

const handleClickoutside = (e) => {
  if (props.canClickOutside && readyForClickOutside.value) {
    if (modal.value) {
      readyForClickOutside.value = false
      e.stopPropagation()
      e.preventDefault()
      close()
    }
  }
}

onBeforeUnmount(() => {
  window.clearTimeout(clickOutsideTimer)

  if (modal.value) {
    restoreDocumentScroll()
    previouslyFocusedElement.value?.focus()
  }
})

defineExpose({ open, close })
</script>

<style lang="scss" scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: $kds-color-line-strong-inverse;
  display: flex;
  transition: opacity 0.3s ease;
  padding: env(safe-area-inset-top) 0 env(safe-area-inset-bottom);
  box-sizing: border-box;

  &__container {
    position: relative;
    margin: auto;
    padding: $kds-space-2x 0 $kds-space-2x 0;
    background-color: #fff;
    box-sizing: border-box;
    max-height: calc(100% - $kds-space-2x);
    max-width: calc(100% - $kds-space-2x);
    overflow-y: auto;
    border-radius: $kds-space-2x;
    box-shadow: 0 0 24px $kds-color-overlay-subtle;
    transition: all 0.3s ease;

    &--header {
      padding: 0 $kds-space-2x 0 $kds-space-2x;
      @include flex-container(row, space-between, md, flex-start);

      &--title {
        margin: 0;
        padding-bottom: $kds-space-2x;
        @include kds-typography-title-large;
      }

      &--end {
        align-items: center;
        display: flex;
        gap: $kds-space-1x;
        margin: -8.5px;
      }
    }

    &--body {
      padding: 0 $kds-space-2x 0 $kds-space-2x;
      max-height: 80vh;
      overflow: auto;
    }

    &--footer {
      padding: $kds-space-2x $kds-space-2x 0 $kds-space-2x;
    }
  }
}

/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
</style>
